import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, ChevronDown, Hash, Loader2, User, X } from "lucide-react";
import { toast } from "react-toastify";
import { OrdersService } from "../../requests/admin/OrdersService";
import OrderStatusBadge from "./OrderStatusBadge";
import { formatCurrency } from "../../utils/formatCurrency";
import { getStatusOptionsWithCurrent } from "../../utils/orderHelpers";

const UpdateOrderStatusModal = ({ isOpen, order, onClose, onUpdated }) => {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && order) {
      setSelectedStatus(order.status || "");
    }
  }, [isOpen, order]);

  if (!isOpen || !order) return null;

  const statusOptions = getStatusOptionsWithCurrent(order.status);
  const hasChanged = selectedStatus && selectedStatus !== order.status;

  const handleSubmit = async () => {
    if (!hasChanged) {
      toast.info("Select a different status to update this order");
      return;
    }

    setIsSubmitting(true);
    const result = await OrdersService.updateOrderStatus(order.order_code, selectedStatus);
    setIsSubmitting(false);

    if (result.success && result.data?.status_code === "000" && !result.data?.in_error) {
      toast.success("Order status updated successfully");
      onUpdated?.(order.order_code, selectedStatus, result.data.data);
      onClose();
    } else {
      toast.error(result.data?.reason || result.message || "Failed to update order status");
    }
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 backdrop-blur-sm bg-black/40"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="flex overflow-auto fixed inset-0 z-50 justify-center items-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
          <div className="flex gap-3 items-center px-6 py-4 border-b border-gray-100">
            <div className="h-8 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full flex-shrink-0" />
            <h3 className="text-lg font-semibold text-gray-900">Update Order Status</h3>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
              <div className="flex gap-1.5 items-center mb-2 text-xs text-gray-500">
                <Hash className="w-3.5 h-3.5" />
                <span className="font-mono">{order.order_code}</span>
              </div>
              {order.user?.full_name && (
                <div className="flex gap-1.5 items-center mb-2 text-sm text-gray-700">
                  <User className="w-3.5 h-3.5 text-gray-400" />
                  {order.user.full_name}
                </div>
              )}
              {order.summary?.total !== undefined && (
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(order.summary.total)}
                </div>
              )}
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold tracking-wide text-gray-400 uppercase">Current Status</p>
              <OrderStatusBadge status={order.status} />
            </div>

            <div>
              <label htmlFor="admin-order-status-select" className="block mb-2 text-sm font-medium text-gray-700">
                New Status
              </label>
              <div className="relative">
                <select
                  id="admin-order-status-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="appearance-none w-full px-4 py-2.5 pr-9 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors cursor-pointer"
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
              </div>
              {!hasChanged && (
                <p className="mt-2 text-xs text-gray-400">Choose a different status to enable the update.</p>
              )}
            </div>
          </div>

          <div className="flex gap-3 justify-end px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !hasChanged}
              className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 -ml-1 w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 w-4 h-4" />
                  Confirm Update
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default UpdateOrderStatusModal;
